import { useCallback, useEffect, useState } from "react";
import { Box, Icon, pickImageFile, toastManager } from "@salestrak/ui";
import { EntityVisual } from "#app/components/entity";
import { UpdateProfilePhotoDialog } from "#app/components/settings/update-profile-photo-dialog";
import { useAvatarUpload } from "#app/lib/hooks/use-avatar-upload";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import { decodeImageOrThrow } from "#app/lib/media-process";
import type { ProfileFormUser } from "#app/lib/data";

// Rendered only inside ProfileForm, so it reads the same fragment rather than
// a narrower copy of it.
export type ProfileAvatarUser = ProfileFormUser;

interface ProfileAvatarProps {
  user: ProfileAvatarUser;
}

/**
 * The editable avatar on the profile settings page: current avatar with a
 * camera badge; clicking it prompts for an image file, then opens the
 * crop-and-zoom dialog whose save runs the photos upload pipeline. That
 * pipeline is the PHOTOS-flagged media store, so with the flag off the avatar
 * is a plain, non-interactive visual: no button, no camera badge.
 */
export function ProfileAvatar({ user }: ProfileAvatarProps) {
  const { ready, hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);
  const { uploadAvatar } = useAvatarUpload();
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Object URLs hold the picked file's bytes until explicitly revoked.
  useEffect(() => {
    return () => {
      if (imageUri) URL.revokeObjectURL(imageUri);
    };
  }, [imageUri]);

  const handlePick = useCallback(async () => {
    const file = await pickImageFile();
    if (!file) return;
    // Gate the format BEFORE opening the cropper. The cropper decodes via an
    // HTMLImageElement, which fails silently on HEIC (the iPhone default) —
    // leaving a blank, dead-end dialog. Reuse the upload pipeline's decode gate
    // so an unsupported file gets the same actionable message here instead.
    try {
      (await decodeImageOrThrow(file)).close();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : "That image isn't supported",
        type: "error",
      });
      return;
    }
    setImageUri(URL.createObjectURL(file));
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(
    (image: Blob) => uploadAvatar(user.userId, image),
    [uploadAvatar, user.userId],
  );

  const visual = (
    <EntityVisual
      entity={{ kind: "user", id: user.userId, user }}
      size="md"
    />
  );

  // `photosEnabled` also reads false while a workspace switch re-resolves the
  // flag set, so the crop dialog stays mounted through that window — dropping
  // it discards an in-progress crop and revokes the picked file's object URL.
  return (
    <>
      {ready && !photosEnabled ? (
        visual
      ) : (
        <Box
          aria-label="Change profile photo"
          as="button"
          cursor="pointer"
          onClick={handlePick}
          position="relative"
          type="button"
        >
          {visual}
          {/* Camera badge — decorative, the whole avatar is the button. Its
            * center sits on the avatar circle's bottom-right diagonal, half over
            * and half outside, which is what the offsets encode. */}
          <Box
            rounded="full" borderWidth={2} align="center"
            background="secondary"
            className="-bottom-1 -right-1 size-4.5 border-background"
            justify="center"
            position="absolute"
            zIndex={10}
          >
            <Icon name="camera" size="xs" />
          </Box>
        </Box>
      )}
      {imageUri ? (
        <UpdateProfilePhotoDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          imageUri={imageUri}
          onSave={handleSave}
        />
      ) : null}
    </>
  );
}
